<template>
    <div
        class="card_thing scrollable-content"
        :class="{ eq_card: activeTab === 'eq' }"
    >
        <div class="tab-body">
            <!-- Tune Tab (from TuneComponent) -->
            <div v-if="activeTab === 'edit'">
                <div class="effect_slider_group">
                    <EffectSlider
                        variant="minimal"
                        :label="$t('effects.pitchshift')"
                        :min="-25"
                        :max="25"
                        :step="1"
                        :modelValue="semitones"
                        @update:modelValue="updateSemitones"
                        :defaultValue="0"
                        :valueLabel="formatSemitones(semitones)"
                    />
                </div>
                <div class="effect_slider_group">
                    <EffectSlider
                        variant="minimal"
                        :label="$t('effects.speed')"
                        :min="0.25"
                        :max="1.75"
                        :step="0.01"
                        :modelValue="tempo"
                        @update:modelValue="$emit('update:tempo', $event)"
                        :defaultValue="1"
                        :valueLabel="formatPercent(tempo)"
                    />
                </div>

                <div class="effect_slider_group">
                    <EffectSlider
                        variant="minimal"
                        :label="$t('effects.balance')"
                        :min="-1"
                        :max="1"
                        :step="0.01"
                        :modelValue="pan"
                        @update:modelValue="$emit('update:pan', $event)"
                        :defaultValue="0"
                        :valueLabel="balanceLabel"
                    />
                </div>

                <div class="effect_slider_group">
                    <EffectSlider
                        variant="minimal"
                        :label="$t('effects.volume')"
                        :min="0"
                        :max="2"
                        :step="0.01"
                        :modelValue="gain"
                        @update:modelValue="$emit('update:gain', $event)"
                        :defaultValue="1"
                        fillFromMin
                        :valueLabel="formatPercent(gain)"
                    />
                </div>
                <div style="height: 1rem;"></div>
            </div>

            <!-- EQ Tab (from EqComponent) -->
            <div v-else-if="activeTab === 'eq'" class="eq-container">
                <EqComponent
                    :low="eq.low"
                    :mid="eq.mid"
                    :high="eq.high"
                    :lowFrequency="eq.lowFrequency ?? 320"
                    :midFrequency="eq.midFrequency ?? 1000"
                    :highFrequency="eq.highFrequency ?? 3200"
                    :lowQ="eq.lowQ ?? 1"
                    :midQ="eq.midQ ?? 1"
                    :highQ="eq.highQ ?? 1"
                    @update:low="$emit('update:eq:low', $event)"
                    @update:mid="$emit('update:eq:mid', $event)"
                    @update:high="$emit('update:eq:high', $event)"
                    @update:lowFrequency="$emit('update:eq:lowFrequency', $event)"
                    @update:midFrequency="$emit('update:eq:midFrequency', $event)"
                    @update:highFrequency="$emit('update:eq:highFrequency', $event)"
                    @update:lowQ="$emit('update:eq:lowQ', $event)"
                    @update:midQ="$emit('update:eq:midQ', $event)"
                    @update:highQ="$emit('update:eq:highQ', $event)"
                />
            </div>

            <!-- FX Tab (from FxComponent) -->
            <div v-else-if="activeTab === 'fx'">
                <div class="effect_slider_group">
                    <EffectSlider
                        variant="minimal"
                        :label="$t('effects.reverberation')"
                        :min="0"
                        :max="1"
                        :step="0.01"
                        :modelValue="reverbMix"
                        @update:modelValue="$emit('update:reverbMix', $event)"
                        :defaultValue="0"
                        :valueLabel="formatPercent(reverbMix)"
                    />
                </div>
                <div class="effect_slider_group">
                    <EffectSlider
                        variant="minimal"
                        :label="$t('effects.distortion')"
                        :min="0"
                        :max="100"
                        :step="1"
                        :modelValue="distortion"
                        @update:modelValue="$emit('update:distortion', $event)"
                        :defaultValue="0"
                        :valueLabel="formatWholePercent(distortion)"
                    />
                </div>

                <div class="effect_slider_group">
                    <EffectSlider
                        variant="minimal"
                        :label="$t('effects.bitcrush')"
                        :min="0"
                        :max="1"
                        :step="0.01"
                        :modelValue="lofiAmount"
                        @update:modelValue="$emit('update:lofiAmount', $event)"
                        :defaultValue="0"
                        :valueLabel="formatPercent(lofiAmount)"
                    />
                </div>

            </div>

            <!-- Presets Tab (from PresetComponent) -->
            <div v-else-if="activeTab === 'presets'">
                <div class="add-preset">
                    <span class="text_sec">{{ $t('effects.saveCurrentEffectsAs') }}</span>
                    <div style="display: flex; gap: 1rem;">
                            <input type="text" class="input_thing" :placeholder="$t('effects.presetName')" v-model="presetname">
                            <div class="btn-new" @click="savePreset"> {{ $t('effects.add') }}</div>
                    </div>
                </div>
                <div class="mt-4">
                    <span class="text_sec">{{ $t('effects.availablePresets') }}</span><br>
                    <div class="presets mt-2">
                        <span v-for="preset in storageState.presets" v-bind:key="preset">
                            <span class="preset-chip" :class="{ 'preset-active': preset.id === activePreset }"
                                @click="setPreset(preset)">
                                {{ preset.name }} <i class="bi bi-x-lg" style="float: right; margin-left: 0.2rem;"
                                    @click="removePreset(preset.id)"></i>
                            </span>
                        </span>
                    </div>
                </div>
            </div>

            <div v-else-if="activeTab === 'export'">
                <slot name="export" />
            </div>

            <div v-else-if="activeTab === 'notes'">
                <slot name="notes" />
            </div>

            <div v-else-if="activeTab === 'metadata'">
                <slot name="metadata" />
            </div>

        </div>
    </div>
    <div class="tab_container mt-4">
        <div class="tab_item" :class="{ tab_item_active: activeTab === 'edit' }" @click="activeTab = 'edit'">
            {{ $t('effects.tune') }}
        </div>
        <div class="tab_item" :class="{ tab_item_active: activeTab === 'eq' }" @click="activeTab = 'eq'">
            EQ
        </div>
        <div class="tab_item" :class="{ tab_item_active: activeTab === 'fx' }" @click="activeTab = 'fx'">
            FX
        </div>
        <div class="tab_item" :class="{ tab_item_active: activeTab === 'presets' }" @click="activeTab = 'presets'">
            {{ $t('effects.presets') }}
        </div>
        <div v-if="showExport" class="tab_item" :class="{ tab_item_active: activeTab === 'export' }" @click="activeTab = 'export'">
            {{ $t('common.export') }}
        </div>
        <div v-if="showNotes" class="tab_item" :class="{ tab_item_active: activeTab === 'notes' }" @click="activeTab = 'notes'">
            {{ $t('common.notes') }}
        </div>
        <div v-if="showMetadata" class="tab_item" :class="{ tab_item_active: activeTab === 'metadata' }" @click="activeTab = 'metadata'">
            {{ $t('common.metadata') }}
        </div>
    </div>
</template>

<script>

import storageService from '@/scripts/services/StorageService';
import EqComponent from './EqComponent.vue';
import { uuidv4 } from '@/scripts/uuid.js';
import EffectSlider from './UI/EffectSlider.vue';

export default {
    name: "EffectsComponent",
    components: {
        EqComponent,
        EffectSlider
    },
    props: {
        // Tune properties
        pitch: {
            type: Number,
            default: 1
        },
        tempo: {
            type: Number,
            default: 1
        },
        gain: {
            type: Number,
            default: 1
        },

        // EQ properties
        eq: {
            type: Object,
            default: () => ({
                low: 0,
                mid: 0,
                high: 0,
                lowFrequency: 320,
                midFrequency: 1000,
                highFrequency: 3200,
                lowQ: 1,
                midQ: 1,
                highQ: 1
            })
        },

        // FX properties
        reverbMix: {
            type: Number,
            default: 0
        },
        distortion: {
            type: Number,
            default: 0
        },
        pan: {
            type: Number,
            default: 0
        },
        lofiAmount: {
            type: Number,
            default: 0
        },
        showExport: {
            type: Boolean,
            default: false
        },
        showNotes: {
            type: Boolean,
            default: false
        },
        showMetadata: {
            type: Boolean,
            default: false
        }
    },
    emits: [
        'update:pitch',
        'update:tempo',
        'update:gain',
        'update:eq:low',
        'update:eq:mid',
        'update:eq:high',
        'update:eq:lowFrequency',
        'update:eq:midFrequency',
        'update:eq:highFrequency',
        'update:eq:lowQ',
        'update:eq:midQ',
        'update:eq:highQ',
        'update:reverbMix',
        'update:distortion',
        'update:pan',
        'update:lofiAmount'
    ],
    data() {
        return {
            activeTab: "edit",
            semitones: 0,
            presetname: '',
            activePreset: null,
        }
    },
    computed: {
        balanceLabel() {
            if (this.pan == 0) return this.$t('effects.center');
            const side = this.pan < 0 ? this.$t('effects.left') : this.$t('effects.right');
            const percentage = Math.abs(this.pan * 100).toFixed(0);
            return `${percentage}% ${side}`;
        },

        storageState() {
            return storageService.state;
        }
    },
    watch: {
        showExport(isAvailable) {
            if (!isAvailable && this.activeTab === 'export') {
                this.activeTab = 'edit';
            }
        },
        showNotes(isAvailable) {
            if (!isAvailable && this.activeTab === 'notes') {
                this.activeTab = 'edit';
            }
        },
        showMetadata(isAvailable) {
            if (!isAvailable && this.activeTab === 'metadata') {
                this.activeTab = 'edit';
            }
        },
        // Watch pitch changes for semitones
        pitch: {
            handler(newPitch) {
                this.semitones = Math.round(Math.log2(newPitch) * 12);
            },
            immediate: true
        }
    },
    methods: {
        formatSemitones(value) {
            const numericValue = Number(value) || 0;
            return `${numericValue > 0 ? '+' : ''}${numericValue.toFixed(0)} st`;
        },
        formatPercent(value) {
            return `${(Number(value) * 100).toFixed(0)}%`;
        },
        formatWholePercent(value) {
            return `${Number(value).toFixed(0)}%`;
        },
        // From TuneComponent
        updateSemitones(value) {
            this.semitones = Number(value);
            const newPitch = Math.pow(2, this.semitones / 12);
            this.$emit('update:pitch', newPitch);
        },

        // From PresetComponent
        setPreset(preset) {
            // Emit all updates to parent
            this.$emit('update:eq:low', preset.low);
            this.$emit('update:eq:mid', preset.mid);
            this.$emit('update:eq:high', preset.high);
            this.$emit('update:eq:lowFrequency', preset.lowFrequency ?? 320);
            this.$emit('update:eq:midFrequency', preset.midFrequency ?? 1000);
            this.$emit('update:eq:highFrequency', preset.highFrequency ?? 3200);
            this.$emit('update:eq:lowQ', preset.lowQ ?? 1);
            this.$emit('update:eq:midQ', preset.midQ ?? 1);
            this.$emit('update:eq:highQ', preset.highQ ?? 1);
            this.$emit('update:reverbMix', preset.reverb);
            this.$emit('update:distortion', preset.distortion);
            this.$emit('update:pan', preset.balance);
            this.$emit('update:gain', preset.gain);
            this.$emit('update:tempo', preset.tempo);
            this.$emit('update:pitch', preset.pitch);

            this.semitones = Math.round(Math.log2(preset.pitch) * 12);
            this.activePreset = preset.id;
        },

        savePreset() {
            if (!this.presetname) return;

            const preset = {
                id: uuidv4(),
                low: this.eq.low,
                mid: this.eq.mid,
                high: this.eq.high,
                lowFrequency: this.eq.lowFrequency ?? 320,
                midFrequency: this.eq.midFrequency ?? 1000,
                highFrequency: this.eq.highFrequency ?? 3200,
                lowQ: this.eq.lowQ ?? 1,
                midQ: this.eq.midQ ?? 1,
                highQ: this.eq.highQ ?? 1,
                reverb: this.reverbMix,
                distortion: this.distortion,
                balance: this.pan,
                gain: this.gain,
                tempo: this.tempo,
                pitch: this.pitch,
                name: this.presetname
            };

            storageService.createPreset(preset);
            this.presetname = '';
        },

        removePreset(uuid) {
            storageService.deletePreset(uuid);
        }
    }
}
</script>

<style scoped>
.scrollable-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.tab-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.scrollable-content::-webkit-scrollbar {
  width: 8px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.add-preset {
    display: flex;
    flex-direction: column;
}

.preset-chip {
    font-family: "Doto", sans-serif;
    font-weight: 900;
    display: inline-block;
    margin-right: 1rem;
    margin-bottom: 1rem;
    font-size: 14px;
    color: #bbb;
    background-color: #1A1A1A;
    padding: 4px 8px;
    border: 1px #2f2f2f;
    border-radius: 6px;
    cursor: pointer;
}

.preset-active {
    background-color: #4d4646;
}

.eq-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.card_thing.eq_card {
  padding: 0;
}

.effect_slider_group {
    margin-bottom: 1rem;
}
</style>
